import { Component, effect, inject } from '@angular/core';
import { NgIf } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { debounceTime } from 'rxjs';
import { GlobalsStore } from '../../../stores/globals.state';
import { CommuneStore } from '../../../stores/commune/commune.state';
import { TopicStore } from '../../../stores/topic/topic.state';
import { TopicHelperService } from '../../../services/topic/topic-helper.service';
import { BookmarkCategoryStore } from '../../../stores/bookmark-category/bookmark-category.state';
import { BookmarkCategory } from '../../../models/bookmark-category';
import { Commune } from '../../../models/commune';
import { DividerModule } from 'primeng/divider';
import { InputSwitchModule } from 'primeng/inputswitch';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { HeaderComponent } from '../../atoms/header/header.component';
import { FormFieldWrapperComponent } from '../../molecules/form-field-wrapper/form-field-wrapper.component';
import { IconDropdownComponent } from '../../atoms/icon-dropdown/icon-dropdown.component';
import {
  PickListModule,
  PickListMoveToSourceEvent,
  PickListMoveToTargetEvent,
} from 'primeng/picklist';

@Component({
  selector: 'app-fallback-topic-form',
  standalone: true,
  imports: [
    NgIf,
    ReactiveFormsModule,
    DividerModule,
    InputSwitchModule,
    InputTextModule,
    InputTextareaModule,
    HeaderComponent,
    FormFieldWrapperComponent,
    IconDropdownComponent,
    PickListModule,
  ],
  templateUrl: './fallback-topic-form.component.html',
  styleUrl: './fallback-topic-form.component.scss',
})
export class FallbackTopicFormComponent {
  readonly globalStore = inject(GlobalsStore);
  readonly communeStore = inject(CommuneStore);
  readonly topicStore = inject(TopicStore);
  readonly topicHelper = inject(TopicHelperService);
  readonly bookmarkCategoryStore = inject(BookmarkCategoryStore);

  activeCommune: Commune | null = null;
  form: FormGroup = this.fb.group({});
  initialized = false;

  /** Bookmark categories currently assigned to a real topic ("Bereiche mit Thema"). */
  availableCategories: BookmarkCategory[] = [];
  /** Bookmark categories with no topic - the actual fallback set ("Bereiche ohne Thema"). */
  unassignedCategories: BookmarkCategory[] = [];

  constructor(private fb: FormBuilder) {
    effect(() => {
      if (!this.activeCommune) {
        this.activeCommune = this.globalStore.activeCommune();
        this.initForm();
      }
    });
  }

  /**
   * Initializes the settings form from the active commune's `atlasConfig.fallbackTopic`.
   * This is not backed by an entity/store item - it's a plain settings object living
   * directly on the commune's atlas configuration.
   *
   * @return {void} Does not return a value.
   */
  initForm(): void {
    const config = this.activeCommune?.atlasConfig?.fallbackTopic;
    this.refreshCategoryLists();

    this.form = this.fb.group({
      title: [config?.title || 'Weitere Bereiche'],
      description: [config?.description || ''],
      icon: [config?.icon || 'layer-group'],
      visible: [config?.visible ?? false],
      spotlight: [config?.spotlight ?? false],
    });

    this.form.valueChanges
      .pipe(debounceTime(300))
      .subscribe(() => this.submit());
    this.initialized = true;
  }

  /**
   * Re-derives the two pick-list sides from the current store state: bookmark categories
   * with a topic vs. without one. Called after every change so the lists always reflect
   * reality rather than the pick-list's own (potentially stale) local drag state.
   *
   * @return {void} Does not return a value.
   */
  refreshCategoryLists(): void {
    this.unassignedCategories = this.topicHelper.getUnassignedBookmarkCategories();
    const unassignedIds = new Set(
      this.unassignedCategories.map(category => category._id)
    );
    this.availableCategories = this.bookmarkCategoryStore
      .asSortedArray()
      .filter(category => !unassignedIds.has(category._id));
  }

  /**
   * Returns a comma-separated list of the topic titles a bookmark category is currently
   * assigned to, for display next to it in the "Bereiche mit Thema" list.
   *
   * @param {string} categoryId - The bookmark category id to look up.
   * @return {string} The joined topic titles, or an empty string if none.
   */
  getTopicTitles(categoryId: string): string {
    return this.topicHelper
      .getTopicsByBookmarkCategoryId(categoryId)
      .map(topic => topic.title)
      .join(', ');
  }

  /**
   * Handles dragging a category into the "Bereiche ohne Thema" side: removes it from
   * whichever real topic(s) it currently belongs to, so it naturally falls into the
   * computed fallback set.
   *
   * @param {PickListMoveToTargetEvent} event - The pick-list move event.
   * @return {void} Does not return a value.
   */
  handleMoveToTarget(event: PickListMoveToTargetEvent): void {
    (event.items as BookmarkCategory[]).forEach(category => {
      this.topicHelper
        .getTopicsByBookmarkCategoryId(category._id)
        .forEach(topic => {
          this.topicStore.update({
            ...topic,
            bookmarkCategories: topic.bookmarkCategories.filter(
              id => id !== category._id
            ),
          });
        });
    });
    this.refreshCategoryLists();
  }

  /**
   * A category has no single topic to reassign it to from here (that's done via the
   * topic's own form), so dragging one back to "Bereiche mit Thema" has no effect -
   * this just re-syncs the lists so it visibly stays in the fallback set.
   *
   * @param {PickListMoveToSourceEvent} event - The pick-list move event.
   * @return {void} Does not return a value.
   */
  handleMoveToSource(event: PickListMoveToSourceEvent): void {
    this.refreshCategoryLists();
  }

  /**
   * Persists the current form value as the commune's `atlasConfig.fallbackTopic` setting.
   *
   * @return {void} Does not return a value.
   */
  submit(): void {
    if (!this.activeCommune) return;
    this.activeCommune.atlasConfig = {
      ...this.activeCommune.atlasConfig,
      fallbackTopic: this.form.value,
    };
    this.communeStore.update(this.activeCommune);
  }
}
